import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'

import { AiDesignerActions } from '@flowx/designer/modules/ai/agents/designer/store/designer.actions'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import { ChatMessage, DataModelDraft } from '@flowx/designer/modules/ai/types/ai.types'

import {
  dataModelDraftHasItems,
  dataModelDraftToRows,
  rowsToDataModelDraft,
} from './adapters/designer-dm-rows.adapter'
import { AiDmKeyRow } from './ai-dm-key-row.model'

export type AiDmSummaryView = {
  rows: AiDmKeyRow[]
}

type DmSummarySource = { draft: DataModelDraft; editable: boolean }

@Injectable({ providedIn: 'root' })
export class AiDmSummaryService {
  #store = inject(Store)

  #catalogue = this.#store.selectSignal(ConversationSelectors.projectDataModelCatalogueFromContext)

  buildSummary(message: ChatMessage): AiDmSummaryView | null {
    const source = this.#summarySource(message)
    return source
      ? { rows: dataModelDraftToRows(source.draft, this.#catalogue(), source.editable) }
      : null
  }

  isEditable(message: ChatMessage): boolean {
    return this.#summarySource(message)?.editable ?? false
  }

  /** Map edited rows back to the draft and persist. Only designer summaries are editable. */
  applyRowsUpdate(message: ChatMessage, rows: AiDmKeyRow[]): void {
    if (!this.isEditable(message)) {
      return
    }
    this.#store.dispatch(
      AiDesignerActions.updateDataModelOnLastMessage({ dataModel: rowsToDataModelDraft(rows) })
    )
  }

  /**
   * Resolves which data-model summary a message carries. Designer summaries
   * (dataModelSummary) are editable; developer summaries (developerDataModelSummary)
   * follow their generated code and are read-only.
   */
  #summarySource(message: ChatMessage): DmSummarySource | null {
    if (dataModelDraftHasItems(message.dataModelSummary)) {
      return { draft: message.dataModelSummary, editable: true }
    }
    if (dataModelDraftHasItems(message.developerDataModelSummary)) {
      return { draft: message.developerDataModelSummary, editable: false }
    }
    return null
  }
}
